import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productService, carModelService, carYearService, carBrandService } from '../services/api';
import { useCart } from '../context/CartContext';
import { useNotification } from '../context/NotificationContext';
import Loading from '../components/Loading';
import { getStatusLabel } from '../utils/productStatus';

const API_BASE_URL = 'https://api.iraq-parts.com';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState('');
  const [compatibility, setCompatibility] = useState({ models: [], brands: [], years: [] });
  const { addToCart } = useCart();
  const { success } = useNotification();
  
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/500x500?text=No+Image';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_BASE_URL}${imageUrl}`;
  };

  const safeJsonParse = (jsonString) => {
    // If already an array, return it
    if (Array.isArray(jsonString)) {
      return jsonString;
    }
    
    // Handle null, undefined, or empty values
    if (!jsonString || jsonString === '' || jsonString === 'null' || jsonString === null || jsonString === undefined) {
      return [];
    }
    
    // If it's a string that looks like an empty object or array, return empty array
    if (typeof jsonString === 'string' && (jsonString.trim() === '{}' || jsonString.trim() === '[]')) {
      return [];
    }
    
    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      // Only log non-empty strings that failed to parse
      if (jsonString && jsonString.toString().trim() !== '') {
        console.warn('Failed to parse JSON:', jsonString, error);
      }
      return [];
    }
  };

  const fetchCompatibilityData = useCallback(async (productData) => {
    try {
      const [modelsRes, brandsRes, yearsRes] = await Promise.all([
        carModelService.getAll(),
        carBrandService.getAll(),
        carYearService.getAll()
      ]);

      const allModels = modelsRes?.data?.data || [];
      const allBrands = brandsRes?.data?.data || [];
      const allYears = yearsRes?.data?.data || [];

      // Parse JSON arrays from product with safe parsing
      const productModels = safeJsonParse(productData.car_models);
      const productBrands = safeJsonParse(productData.car_brands);
      const productYears = safeJsonParse(productData.car_years);

      // Filter to get matching items
      const compatibleModels = allModels.filter(m => 
        productModels.includes(String(m.id)) || productModels.includes(m.id)
      );
      const compatibleBrands = allBrands.filter(b => 
        productBrands.includes(String(b.id)) || productBrands.includes(b.id)
      );
      const compatibleYears = allYears.filter(y => 
        productYears.includes(String(y.id)) || productYears.includes(y.id)
      );

      setCompatibility({
        models: compatibleModels,
        brands: compatibleBrands,
        years: compatibleYears
      });
    } catch (error) {
      console.error('Error fetching compatibility data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productService.getById(id);
        console.log('Product Detail Response:', response);
        const productData = response?.data?.data || null;
        console.log('Product Data:', productData);
        setProduct(productData);
        setMainImage(productData?.image_url || '');
        
        // Fetch compatibility data if product has car details
        if (productData) {
          await fetchCompatibilityData(productData);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        console.error('Error details:', error.response || error.message);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, fetchCompatibilityData]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    success(`تم إضافة ${product.name} إلى السلة`);
  };

  const handleDirectCheckout = () => {
    addToCart(product, quantity);
    success(`تم إضافة ${product.name} إلى السلة`);
    navigate('/checkout');
  };

  if (loading) return <Loading />;
  if (!product) return <div className="container mx-auto px-4 py-8">Product not found</div>;

  const hasCompatibilityData = compatibility.models.length > 0 || 
                                 compatibility.brands.length > 0 || 
                                 compatibility.years.length > 0;

  return (
    <div className="container mx-auto px-4 py-8" dir="rtl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Images */}
        <div>
          <div className="mb-4">
            <img
              src={getImageUrl(mainImage)}
              alt={product.name}
              className="w-full rounded-lg shadow-lg"
            />
          </div>
          
          {/* Additional Images Gallery */}
          {product.images && product.images.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {/* Main image thumbnail */}
              <div 
                className="cursor-pointer border-2 rounded-lg overflow-hidden hover:border-primary transition"
                style={{ borderColor: mainImage === product.image_url ? '#3b82f6' : '#e5e7eb' }}
                onClick={() => setMainImage(product.image_url)}
              >
                <img
                  src={getImageUrl(product.image_url)}
                  alt={product.name}
                  className="w-full h-20 object-cover"
                />
              </div>
              
              {/* Additional images thumbnails */}
              {product.images.map((img, index) => (
                <div
                  key={index}
                  className="cursor-pointer border-2 rounded-lg overflow-hidden hover:border-primary transition"
                  style={{ borderColor: mainImage === img.image_url ? '#3b82f6' : '#e5e7eb' }}
                  onClick={() => setMainImage(img.image_url)}
                >
                  <img
                    src={getImageUrl(img.image_url)}
                    alt={`${product.name} - صورة إضافية ${index + 1}`}
                    className="w-full h-20 object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Product Details */}
        <div>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-4 text-right">{product.name}</h1>
            </div>
            {product.product_status && (
              <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold ml-4">
                {getStatusLabel(product.product_status)}
              </span>
            )}
          </div>
          
          {product.sku && (
            <p className="text-gray-600 mb-2 text-right">
              <span className="font-semibold">كود المنتج:</span> {product.sku}
            </p>
          )}
          
          {product.category_name && (
            <p className="text-gray-600 mb-4 text-right">
              <span className="font-semibold">القسم:</span> {product.category_name}
            </p>
          )}

          <div className="border-t border-b py-4 mb-4">
            <p className="text-4xl font-bold text-primary mb-2 text-right">
              {product.sell_price} د.ع
            </p>
            
            {product.stock_quantity > 0 ? (
              <p className="text-green-600 font-semibold text-right">
                ✓ متوفر
              </p>
            ) : (
              <p className="text-red-600 font-semibold text-right">
                ✗ غير متوفر
              </p>
            )}
          </div>

          {product.description && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2 text-right">الوصف:</h3>
              <p className="text-gray-700 text-right">
                {product.description}
              </p>
            </div>
          )}

          {product.stock_quantity > 0 && (
            <div className="flex items-center gap-4 mb-6 justify-end">
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                  className="px-4 py-2 hover:bg-gray-100"
                >
                  +
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 text-center border-x border-gray-300 py-2"
                  min="1"
                  max={product.stock_quantity}
                />
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 hover:bg-gray-100"
                >
                  -
                </button>
              </div>
              <label className="font-semibold">الكمية:</label>
            </div>
          )}

          {product.stock_quantity > 0 && (
            <>
              <button
                onClick={handleAddToCart}
                className="w-full bg-primary text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 mb-3"
              >
                أضف إلى السلة
              </button>
              <button
                onClick={handleDirectCheckout}
                className="w-full bg-green-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-green-700"
              >
                اشتر الآن
              </button>
            </>
          )}
        </div>
      </div>

      {/* Compatibility Table */}
      {hasCompatibilityData && (
        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6 text-right">السيارات المتوافقة مع هذا المنتج</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" dir="rtl">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="border border-gray-300 px-4 py-3 text-right">الموديل</th>
                  <th className="border border-gray-300 px-4 py-3 text-right">الماركة</th>
                  <th className="border border-gray-300 px-4 py-3 text-right">السنوات المتوافقة</th>
                </tr>
              </thead>
              <tbody>
                {compatibility.models.length > 0 ? (
                  // Show each model with its years
                  compatibility.models.map((model, index) => {
                    const modelBrand = compatibility.brands.find(b => 
                      b.id === model.brand_id || b.name === model.brand_name
                    );
                    
                    return (
                      <tr key={model.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="border border-gray-300 px-4 py-3 text-right">
                          <div className="flex items-center gap-2 justify-end">
                            {model.image_url && (
                              <img 
                                src={getImageUrl(model.image_url)} 
                                alt={model.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                            <span className="font-semibold">{model.name}</span>
                          </div>
                        </td>
                        <td className="border border-gray-300 px-4 py-3 text-right">
                          <span className="font-medium text-gray-700">
                            {modelBrand?.name || model.brand_name || '-'}
                          </span>
                        </td>
                        <td className="border border-gray-300 px-4 py-3">
                          {compatibility.years.length > 0 ? (
                            <div className="flex flex-wrap gap-2 justify-end">
                              {compatibility.years.map(year => (
                                <span 
                                  key={year.id} 
                                  className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium"
                                >
                                  {year.year}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-500">جميع السنوات</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : compatibility.brands.length > 0 ? (
                  // Show brands only if no models specified
                  compatibility.brands.map((brand, index) => (
                    <tr key={brand.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="border border-gray-300 px-4 py-3 text-right text-gray-500">
                        جميع الموديلات
                      </td>
                      <td className="border border-gray-300 px-4 py-3 text-right">
                        <span className="font-semibold text-gray-700">{brand.name}</span>
                      </td>
                      <td className="border border-gray-300 px-4 py-3">
                        {compatibility.years.length > 0 ? (
                          <div className="flex flex-wrap gap-2 justify-end">
                            {compatibility.years.map(year => (
                              <span 
                                key={year.id} 
                                className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium"
                              >
                                {year.year}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">جميع السنوات</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : compatibility.years.length > 0 ? (
                  // Show years only if no models or brands specified
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 text-right text-gray-500">
                      جميع الموديلات
                    </td>
                    <td className="border border-gray-300 px-4 py-3 text-right text-gray-500">
                      جميع الماركات
                    </td>
                    <td className="border border-gray-300 px-4 py-3">
                      <div className="flex flex-wrap gap-2 justify-end">
                        {compatibility.years.map(year => (
                          <span 
                            key={year.id} 
                            className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {year.year}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* Summary Section */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-primary">{compatibility.models.length}</div>
              <div className="text-sm text-gray-600 mt-1">موديل متوافق</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600">{compatibility.brands.length}</div>
              <div className="text-sm text-gray-600 mt-1">ماركة متوافقة</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600">{compatibility.years.length}</div>
              <div className="text-sm text-gray-600 mt-1">سنة متوافقة</div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border-r-4 border-yellow-500 rounded-lg text-right">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold text-yellow-800 mb-1">تنويه هام:</p>
                <p className="text-sm text-gray-700">
                  يرجى التأكد من توافق القطعة مع موديل وسنة سيارتك قبل الشراء. في حال وجود أي استفسارات، لا تتردد في التواصل معنا عبر واتساب.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
